import { makeStyles } from 'tss-react/mui';
import {
  P2GBox,
  P2GButton,
  P2GCardContainer,
  P2GGrid,
  P2GIcon,
  P2GLink,
  P2GText,
  P2GTextField,
  SvgStarHalf,
  SvgTickCircle,
  useP2GMediaQueryOnly
} from '@parcel2go/core-ui';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { GenericErrorResponse } from '~/lib/types/basket-types';
import { useTenant } from '@parcel2go/npm-p2g-app';
import { PublicSettings } from '~/lib/ui-serverless-settings';

const useStyles = makeStyles()((theme) => ({
  breakdown: {
    '& > .MuiCardContent-root': {
      padding: theme.spacing(3) + '!important'
    }
  }
}));

type props = {
  wrapper: (c: React.ReactNode) => React.ReactNode;
  addDiscountCode: (code: string) => void;
  discountCode?: string;
  discount?: number;
  warnings?: GenericErrorResponse[];
};

function DiscountCode({ addDiscountCode, discountCode, discount, warnings, wrapper }: props) {
  const { settings } = useTenant();
  const { classes } = useStyles();
  const isXs = useP2GMediaQueryOnly('xs');
  const [open, setOpen] = useState((discountCode?.length || 0) > 0);
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const [code, setCode] = useState(discountCode || '');
  const applyDiscount = () => addDiscountCode(code);
  const error = warnings?.some((x) => x.type === 'invalid-discount-code');
  const showDiscountInputField = settings?.[PublicSettings.ORDERPROCESS.SHOWDISCOUNTINPUTFIELD] === 'true';

  const reset = () => {
    setOpen(false);
    setCode('');
    addDiscountCode('');
  };

  const tryAgain = () => {
    setCode('');
    addDiscountCode('');
  };

  if (settings?.[PublicSettings.ORDERPROCESS.DISCOUNTSENABLED] !== 'true') {
    return null;
  }

  if (!open && !showDiscountInputField) {
    return wrapper(
      <P2GBox mt={2}>
        <P2GText component="span" variant="body2">
          <Trans
            i18nKey="BASKET_DISCOUNT_CODE_CLOSED_MESSAGE"
            t={t}
            defaults="<0>Click here</0> to enter a discount code"
            components={[<P2GLink onClick={() => setOpen(true)} />]}
          />
        </P2GText>
      </P2GBox>
    );
  }

  if (error) {
    return wrapper(
      <P2GCardContainer classNames={[classes.breakdown]}>
        <P2GGrid container gap={2}>
          <P2GGrid item alignSelf="center">
            <P2GIcon size="xs" palette="error">
              <SvgStarHalf />
            </P2GIcon>
          </P2GGrid>
          <P2GGrid item flexGrow={1}>
            <P2GText component="span" variant="body2" colour="error" fontWeight="bold">
              <Trans
                i18nKey="BASKET_DISCOUNT_CODE_INPUT_INVALID_ERROR"
                t={t}
                defaults="Invalid discount code"
                components={[<P2GText variant="body2" fontWeight="bold" />]}
                values={{ code: code }}
              />
            </P2GText>
          </P2GGrid>
          <P2GGrid item>
            <P2GText variant="body2">
              <P2GLink onClick={tryAgain}>
                {t('BASKET_DISCOUNT_TRY_ANOTHER_CODE_BUTTON', { defaultValue: 'Try Another Code' })}
              </P2GLink>
            </P2GText>
          </P2GGrid>
        </P2GGrid>
      </P2GCardContainer>
    );
  }

  if (discount && discount > 0) {
    return wrapper(
      <P2GCardContainer classNames={[classes.breakdown]}>
        <P2GGrid container gap={2}>
          <P2GGrid item alignSelf="center">
            <P2GIcon size="xs" palette="success">
              <SvgTickCircle />
            </P2GIcon>
          </P2GGrid>
          <P2GGrid item flexGrow={1}>
            <P2GText component="span" variant="body2" fontWeight="bold">
              <Trans
                i18nKey="BASKET_DISCOUNT_CODE_DISCOUNTED_MESSAGE"
                t={t}
                defaults="Discount code '{{code}}' applied"
                components={[<P2GText variant="body2" fontWeight="bold" />]}
                values={{ code: code }}
              />
            </P2GText>
          </P2GGrid>
          <P2GGrid item>
            <P2GText variant="body2">
              <P2GLink onClick={reset}>
                {t('BASKET_DISCOUNT_REMOVE_CODE_BUTTON', { defaultValue: 'Remove Discount' })}
              </P2GLink>
            </P2GText>
          </P2GGrid>
        </P2GGrid>
      </P2GCardContainer>
    );
  }

  return wrapper(
    <P2GGrid container gap={2}>
      <P2GGrid item flexGrow={1}>
        <P2GGrid container gap={2} wrap="nowrap" alignItems="center">
          <P2GGrid item>
            <P2GIcon size="xs">
              <SvgStarHalf />
            </P2GIcon>
          </P2GGrid>
          <P2GGrid item flexGrow={1}>
            <P2GTextField
              autoFocus
              id="txtDiscountCode"
              label={t('BASKET_DISCOUNT_CODE_INPUT_LABEL', { defaultValue: 'Enter discount code' })}
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
              fullWidth
            />
          </P2GGrid>
        </P2GGrid>
      </P2GGrid>
      <P2GGrid item sx={{ width: { xs: '100%', sm: 'auto' } }}>
        <P2GButton
          size={isXs ? 'large' : undefined}
          fullWidth
          disabled={(code || '').length < 4}
          sx={{ display: 'flex', height: '100%' }}
          variant="tertiary"
          onClick={applyDiscount}
        >
          {t('BASKET_DISCOUNT_CODE_BUTTON', { defaultValue: 'Apply Code' })}
        </P2GButton>
      </P2GGrid>
    </P2GGrid>
  );
}

export default DiscountCode;
